import { Injectable } from '@angular/core';
import { mergeMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { FirebaseService } from './firebase.service';
import { HttpClient } from '@angular/common/http';
import { ApiProduct } from './api-products.service';
import { Tenant } from './tenants.service';

export type ApiSubscription = {
  id: string;
  tenant_id: string;
  api_product_id: string;
  start_date: string;
  active: boolean;
  created_at: string;
  updated_at: string;
  updated_by: string;
  deleted: boolean;
  deleted_at: string;
  deleted_by: string;
  notes?: string;
};

export type ApiSubscriptionResponse = {
  api_subscription: ApiSubscription;
  api_product: ApiProduct;
};

export type ApiSubscriptionDetails = {
  api_subscription: ApiSubscription;
  api_product: ApiProduct;
  tenant: Tenant;
};

export type CreateApiSubscriptionRequest = {
  tenant_id: string;
  api_product_id: string;
  start_date: Date;
  active: boolean;
};

export type UpdateApiSubscriptionRequest = {
  id: string;
  start_date: Date;
  active: boolean;
  notes?: string;
};

export type DeleteApiSubscriptionResponse = {
  message: string;
};

@Injectable({
  providedIn: 'root',
})
export class ApiSubscriptionsService {
  private getApiSubscriptionByTenantIdUrl =
    environment.serverUrl + '/api-subscriptions/for-tenant';
  private getApiSubscriptionDetailsUrl =
    environment.serverUrl + '/api-subscriptions/get';
  private createApiSubscriptionUrl =
    environment.serverUrl + '/api-subscriptions/create';
  private updateApiSubscriptionUrl =
    environment.serverUrl + '/api-subscriptions/update';
  private deleteApiSubscriptionUrl =
    environment.serverUrl + '/api-subscriptions/delete';

  constructor(
    private firebaseService: FirebaseService,
    private http: HttpClient
  ) {}

  getApiSubscriptionByTenantId(tenantId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<ApiSubscriptionResponse | null>(
          this.getApiSubscriptionByTenantIdUrl + '/' + tenantId,
          {
            headers: { Authorization: 'Bearer ' + token },
          }
        );
      })
    );
  }

  getApiSubscriptionDetails(id: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<ApiSubscriptionDetails>(
          this.getApiSubscriptionDetailsUrl + '/' + id,
          {
            headers: { Authorization: 'Bearer ' + token },
          }
        );
      })
    );
  }

  createApiSubscription(request: CreateApiSubscriptionRequest) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<ApiSubscription>(
          this.createApiSubscriptionUrl,
          request,
          { headers: { Authorization: 'Bearer ' + token } }
        );
      })
    );
  }

  updateApiSubscription(id: string, request: UpdateApiSubscriptionRequest) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.put<ApiSubscription>(
          this.updateApiSubscriptionUrl + '/' + id,
          request,
          { headers: { Authorization: 'Bearer ' + token } }
        );
      })
    );
  }

  deleteApiSubscription(id: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.delete<DeleteApiSubscriptionResponse>(
          this.deleteApiSubscriptionUrl + '/' + id,
          { headers: { Authorization: 'Bearer ' + token } }
        );
      })
    );
  }
}
